import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { tradeMethodsApi } from 'api';
import { PageHeader, Dialog, DataWrapper } from 'components';
import { QueryKey } from 'enums';
import {
  DeeplinksList,
  DeeplinksDetailsForm,
  DeeplinksProvider,
} from 'features/deeplinks';
import { useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Deeplink } from 'types';

export const TradeMethodDeeplinksPage = () => {
  const { id: tradeMethodId = '' } = useParams<{ id: string }>();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.deeplinks',
  });
  const { getTradeMethodLabel } = useUserContextUtils();

  const [isDetailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDeeplink, setSelectedDeeplink] =
    React.useState<Deeplink | null>(null);

  const tradeMethodQuery = useQuery(
    [QueryKey.TradeMethods, tradeMethodId],
    () => tradeMethodsApi.getOne(tradeMethodId!),
  );

  const subtitle = useMemo(
    () => getTradeMethodLabel(tradeMethodId),
    [getTradeMethodLabel, tradeMethodId],
  );

  const onEdit = useCallback((deeplink: Deeplink) => {
    setDetailsDialogOpen(true);
    setSelectedDeeplink(deeplink);
  }, []);

  const onCreate = useCallback(() => {
    setDetailsDialogOpen(true);
    setSelectedDeeplink(null);
  }, []);

  const onClose = useCallback(() => {
    setDetailsDialogOpen(false);
    setSelectedDeeplink(null);
  }, []);

  return (
    <Fragment>
      <PageHeader
        title={t('title')}
        subtitle={subtitle}
        divider={false}
        rightContentButton={{
          onClick: onCreate,
        }}
      />
      <DataWrapper queryResult={tradeMethodQuery}>
        <DeeplinksProvider tradeMethod={tradeMethodQuery.data}>
          <div className="tw-py-8">
            <DeeplinksList onEdit={onEdit} />
          </div>
          <Dialog
            title={
              selectedDeeplink
                ? t('details_form.title.edit')
                : t('details_form.title.create')
            }
            open={isDetailsDialogOpen}
            onClose={onClose}
            maxWidth="sm"
            modal
          >
            <DeeplinksDetailsForm
              onSubmit={onClose}
              selectedDeeplink={selectedDeeplink}
            />
          </Dialog>
        </DeeplinksProvider>
      </DataWrapper>
    </Fragment>
  );
};
